import { create } from "zustand";
import axios from "axios";
import { MAIN_URL } from "../../Configurations/Urls";

let inFlightFetch = null;
let inFlightFetchUserId = null;

const usePermissionDataStore = create((set, get) => ({
  Permission: [],
  permissionOrgUserId: null,
  isPermissionLoaded: false,
  isPermissionFetching: false,
  permissionsFetchedAt: null,

  setPermission: (data, orgUserId) =>
    set({
      Permission: Array.isArray(data) ? data : [],
      permissionOrgUserId:
        orgUserId != null ? Number(orgUserId) : get().permissionOrgUserId,
      isPermissionLoaded: true,
      isPermissionFetching: false,
      permissionsFetchedAt: Date.now(),
    }),

  resetPermissions: () => {
    inFlightFetch = null;
    inFlightFetchUserId = null;
    set({
      Permission: [],
      permissionOrgUserId: null,
      isPermissionLoaded: false,
      isPermissionFetching: false,
      permissionsFetchedAt: null,
    });
  },

  invalidatePermissions: () => {
    inFlightFetch = null;
    inFlightFetchUserId = null;
    set({
      isPermissionLoaded: false,
      isPermissionFetching: false,
      permissionsFetchedAt: null,
    });
  },

  updatePermissionStore: (updatedList, orgUserId) =>
    set({
      Permission: updatedList,
      permissionOrgUserId:
        orgUserId != null ? Number(orgUserId) : get().permissionOrgUserId,
      isPermissionLoaded: true,
      isPermissionFetching: false,
      permissionsFetchedAt: Date.now(),
    }),

  hasPermissionsForUser: (orgUserId) => {
    if (orgUserId == null) return false;
    const state = get();
    return (
      state.isPermissionLoaded &&
      Number(state.permissionOrgUserId) === Number(orgUserId) &&
      Array.isArray(state.Permission)
    );
  },

  fetchPermissions: async (
    organizationUserId,
    { force = false, background = false } = {}
  ) => {
    if (!organizationUserId) {
      console.warn("Organization User ID is required to fetch permissions");
      return null;
    }

    const orgUserId = Number(organizationUserId);
    const state = get();
    const sameUser = Number(state.permissionOrgUserId) === orgUserId;

    if (!force && sameUser && state.isPermissionLoaded && inFlightFetch) {
      return inFlightFetch;
    }

    if (!force && sameUser && state.isPermissionLoaded && !inFlightFetch) {
      return state.Permission;
    }

    if (force) {
      inFlightFetch = null;
      inFlightFetchUserId = null;
    }

    if (!force && inFlightFetch && inFlightFetchUserId === orgUserId) {
      return inFlightFetch;
    }

    inFlightFetchUserId = orgUserId;
    // Don't flash full-page loading for background updates
    if (!background && !(sameUser && state.isPermissionLoaded)) {
      set({ isPermissionFetching: true });
    }

    inFlightFetch = (async () => {
      try {
        const res = await axios.get(
          `${MAIN_URL}/api/user/${orgUserId}/check-permission-access`,
          {
            params: force ? { _t: Date.now() } : undefined,
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
              "Cache-Control": "no-cache",
              Pragma: "no-cache",
            },
          }
        );

        const allowed = res?.data?.allowed_actions || [];
        set({
          Permission: allowed,
          permissionOrgUserId: orgUserId,
          isPermissionLoaded: true,
          isPermissionFetching: false,
          permissionsFetchedAt: Date.now(),
        });
        return allowed;
      } catch (error) {
        set({ isPermissionFetching: false });
        throw error;
      } finally {
        if (inFlightFetchUserId === orgUserId) {
          inFlightFetch = null;
          inFlightFetchUserId = null;
        }
      }
    })();

    return inFlightFetch;
  },

  refreshPermissions: async (organizationUserId, { silent = false } = {}) => {
    if (!silent) {
      get().invalidatePermissions();
    }
    const allowed = await get().fetchPermissions(organizationUserId, {
      force: true,
      background: silent,
    });
    if (!silent && allowed) {
      console.log("Permissions refreshed:", allowed);
    }
    return allowed;
  },
}));

export const notifyPermissionsChanged = () => {
  window.dispatchEvent(new CustomEvent("hrerp:permissions-changed"));
};

export default usePermissionDataStore;
